import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Container } from '@mui/material';
import { Instagram, YouTube } from '@mui/icons-material';

// `inline-block py-1` lifts the tap target from 16px to 28px tall. As bare
// inline anchors these sat well under the 24px minimum (WCAG 2.2 AA 2.5.8,
// Target Size) and were fiddly to hit on a phone — they were the single most
// common offender on the site, since the footer is on every page. The list's
// `space-y-2` drops to `space-y-1` below so the visual rhythm is unchanged.
const FOOTER_LINK_CLS = 'inline-block py-1 text-sm text-blue-100 hover:text-white transition-colors';

const FooterLink = ({ to, children, external = false }) => {
  const { pathname } = useLocation();

  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={FOOTER_LINK_CLS}
      >
        {children}
      </a>
    );
  }

  const linkPath = to.split('?')[0];
  const isSamePage = pathname === linkPath;

  const handleClick = (e) => {
    if (isSamePage) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      className={FOOTER_LINK_CLS}
    >
      {children}
    </Link>
  );
};

const FooterSection = ({ title, links }) => (
  <div>
    <h3 className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: '#B48CE6' }}>
      {title}
    </h3>
    <ul className="space-y-1">
      {links.map((link, index) => (
        <li key={index}>
          <FooterLink to={link.to} external={link.external}>
            {link.label}
          </FooterLink>
        </li>
      ))}
    </ul>
  </div>
);

// Platform-brand colors for social icons — an accepted exception to the
// talexo-blue/purple palette, same reasoning as keeping Google's own blue
// on the "Continue with Google" button.
// LinkedIn was listed with `href: null`, which the renderer below turns into a
// permanently greyed-out, `pointer-events-none` icon — a control that looked
// broken and could never be clicked. Dropped until there is a real URL; add it
// back as a normal entry once the profile exists.
/*
 * The BARE profile URLs, with no share token on the end.
 *
 * Instagram links copied from inside the app carry a per-share token — `stkn`,
 * or `igsh` on an older copy. Pasted into a public site it does not merely
 * track: Instagram greets every visitor with a modal reading
 * "<the person who copied the link> shared this with you" over a Follow button
 * for THEIR personal account, before the visitor ever sees Talexo's profile.
 * The token belongs to whoever pressed Share, not to the profile.
 *
 * So the href is the canonical profile address and nothing else. It is also the
 * one form that cannot go stale — a share token is minted per copy and is not
 * ours to keep working.
 */
const socialLinks = [
  { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/talexo_official', className: 'bg-gradient-to-tr from-[#FED373] via-[#E1306C] to-[#833AB4]' },
  { icon: YouTube, label: 'YouTube', href: 'https://youtube.com/@talexoj?si=aU1_6rsBvDsoSnR9', className: 'bg-[#FF0000]' },
];

const Footer = () => {
  const informationLinks = [
    { to: '/about-us', label: 'About Us' },
    // Careers is disabled for now (see src/routes/index.jsx) — leaving the link
    // in would send visitors to a route that no longer resolves.
    // { to: '/careers', label: 'Careers with Us' },
    { to: '/contact-us', label: 'Contact Us' },
    { to: '/faqs', label: 'FAQs' },
  ];

  const jobseekersLinks = [
    { to: '/register', label: 'Register' },
    { to: '/jobs', label: 'Search Jobs' },
    { to: '/login?type=jobseeker', label: 'Job Seeker Login' },
    // "Report a Problem" removed: it pointed at /contact-us, the exact same
    // destination as "Contact Us" in the Information column — one page reached
    // by two differently-labelled links.
  ];

  const employersLinks = [
    { to: '/employer/post-jobs', label: 'Post a Job' },
    // "Access Database" removed — the page it pointed at was a duplicate of the
    // Employer Services pitch, and the name matched nothing in the product
    // (the feature is called "Candidates" once you are signed in).
    { to: '/employer/recruiter-login', label: 'Recruiter Login' },
    // { to: '/employer/recruiter-plans', label: 'Recruiter Plans' },
  ];

  const browseJobsLinks = [
    { to: '/jobs?browse=all', label: 'Browse All Jobs' },
    { to: '/jobs-by-category', label: 'Jobs by Category' },
    { to: '/jobs-by-location', label: 'Jobs by Location' },
    { to: '/jobs-by-designation', label: 'Jobs by Designation' },
  ];

  // Deeper than the -dark palette tokens on their own: those sit close to the
  // hero gradient, so the footer read as another banded section rather than the
  // base of the page. Same blue→purple ramp, taken a few steps darker.
  return (
    <footer
      className="relative text-white overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #05506D 0%, #2E1250 100%)' }}
    >
      <div className="relative z-10">
      <Container maxWidth="xl">
        <div className="py-6">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-x-6 gap-y-5">
            {/* Logo + compact contact line */}
            <div className="col-span-2">
              {/* logo192.png is the icon alone (no wordmark baked in, unlike jeminy_logo.png) —
                  paired with real white text here so it stays legible on this dark background
                  instead of the PNG's built-in dark-gray "Talexo" text. */}
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo192.png" alt="" className="h-8 w-8" />
                <span className="text-xl font-bold text-white">Talexo</span>
              </div>
              <div className="text-sm text-blue-100 leading-relaxed space-y-1">
                <p>New Delhi, India</p>
                {/* Phone number commented out for now — see task from Sep 2026 */}
                {/* <p>
                  <a href="tel:+916399123567" className="inline-block py-1 hover:text-white transition-colors">+91 6399123567</a>
                </p> */}
                <p>
                  <a href="mailto:info@talexo.co.in" className="inline-block py-1 hover:text-white transition-colors">info@talexo.co.in</a>
                </p>
              </div>
            </div>

            <FooterSection title="Information" links={informationLinks} />
            <FooterSection title="Jobseekers" links={jobseekersLinks} />
            <FooterSection title="Employers" links={employersLinks} />
            <FooterSection title="Browse Jobs" links={browseJobsLinks} />
          </div>
        </div>
      </Container>

      {/* Bottom bar — copyright, social icons, and legal links in one compact row */}
      <div className="bg-black/15 border-t border-white/20 py-3">
        <Container maxWidth="xl">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
            <p className="text-xs text-blue-100">
              Copyright &copy; {new Date().getFullYear()} Talexo. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {socialLinks.map(({ icon: Icon, label, href, className }) => (
                  <a
                    key={label}
                    href={href || undefined}
                    target={href ? '_blank' : undefined}
                    rel={href ? 'noopener noreferrer' : undefined}
                    aria-label={label}
                    className={`w-7 h-7 rounded-md flex items-center justify-center text-white transition-opacity ${
                      href ? `${className} hover:opacity-80 cursor-pointer` : 'bg-white/10 opacity-40 cursor-not-allowed pointer-events-none'
                    }`}
                  >
                    <Icon sx={{ fontSize: 14 }} />
                  </a>
                ))}
              </div>
              <span className="text-white/30 hidden sm:inline">|</span>
              <div className="flex items-center gap-4 text-xs">
                <Link to="/privacy-policy" className="inline-block py-1 text-blue-100 hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <span className="text-white/30">•</span>
                <Link to="/terms-and-conditions" className="inline-block py-1 text-blue-100 hover:text-white transition-colors">
                  Terms & Conditions
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </div>
      </div>
    </footer>
  );
};

export default Footer;
